/**
 * Redux slice for managing the state of sub-quest reports.
 *
 * This slice handles:
 * - Fetching sub-quest details, progressions, logs, and progression counts.
 * - Managing the state of these entities, including their loading and error statuses.
 * - Removing logs when a progression is deleted.
 *
 * The slice leverages Redux Toolkit's `createSlice` for concise and maintainable state management.
 */

// Import necessary modules from Redux Toolkit
import { createSlice } from '@reduxjs/toolkit';

// Import constants and actions
import { ReducerStatus } from 'utils/constants';
import {
  deleteSubQuestProgressionAction,
  deleteSubQuestProgressionSessionsAction,
  getSubQuestByIdAction,
  getSubQuestLogsAction,
  getSubQuestProgressionsAction,
  getSubQuestProgressionsCountAction,
  getSubQuestProgressionSessionsAction,
  getSubQuestProgressionSessionAction,
  updateQuizSessionAction,
  getBulkFileSubmissionsAction,
  getProgressionUserIdsAction,
  importBulkFileSubmissionsAction,
} from './subQuest-report-action';

// Create a slice for managing sub-quest report state
const subQuestReportSlice = createSlice({
  name: 'subQuestReport',
  initialState: {
    subQuest: null, // Stores sub-quest details
    progressions: [], // Stores progressions data
    progressionMetaData: null, // Metadata for progressions
    progressionsCount: null, // Count of progressions
    logs: [], // Stores logs data
    progressionSessions: [], // Stores progression sessions data
    progressionSession: null, // Stores a single progression session
    getSubQuest: { status: ReducerStatus.IDLE, error: null }, // Status for fetching sub-quest details
    getProgressionsCount: { status: ReducerStatus.IDLE, error: null }, // Status for fetching progression count
    getProgressions: { status: ReducerStatus.IDLE, error: null }, // Status for fetching progressions
    getLogs: { status: ReducerStatus.IDLE, error: null }, // Status for fetching logs
    getProgressionSessions: {
      status: ReducerStatus.IDLE,
      modal: false,
      subQuestId: null,
      progressionId: null,
      error: null,
    }, // Status for fetching progression sessions
    getProgressionSession: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    getQuizSession: {
      status: ReducerStatus.IDLE,
      error: null,
    },
    deleteProgression: {
      modal: false, // Modal visibility for deleting progression
      subQuestId: null, // ID of the sub-quest to delete progression from
      progressionId: null, // ID of the progression to delete
      status: ReducerStatus.IDLE, // Status of the delete operation
      error: null, // Error message for delete operation
    },
    updateQuizSession: {
      modal: false,
      subQuestId: null,
      progressionId: null,
      sessionId: null,
      status: ReducerStatus.IDLE,
      error: null,
    },
    deleteProgressionSession: {
      modal: false, // Modal visibility for deleting progression session
      subQuestId: null, // ID of the sub-quest to delete session from
      progressionId: null, // ID of the progression to delete session from
      sessionId: null, // ID of the session to delete
      status: ReducerStatus.IDLE, // Status of the delete operation
      error: null, // Error message for delete operation
    },
    exportSubmissionSessions: {
      modal: false,
      userIds: [],
      subQuestId: null,
      data: [],
      status: ReducerStatus.IDLE,
      error: null,
    },
    getProgressionUserIds: {
      status: ReducerStatus.IDLE,
      error: null,
      data: [],
    },
    importSubmissionSessions: {
      modal: false,
      subQuestId: null,
      status: ReducerStatus.IDLE,
      error: null,
      result: null,
    },
  },
  reducers: {
    // Reducer to toggle the delete progression modal
    toggleDeleteProgressionModal(state, action) {
      state.deleteProgression.modal = action.payload;
    },
    // Reducer to toggle the get progression sessions modal
    toggleProgressionSessionsModal(state, action) {
      state.getProgressionSessions.modal = action.payload;
    },
    // Reducer to set values for deleting a progression session
    toggleDeleteProgressionSessionModal(state, action) {
      state.deleteProgressionSession.modal = action.payload;
    },
    // Reducer to set values for fetching sub-quest by ID
    setGetProgressionSessionsValue(state, action) {
      const { subQuestId, progressionId } = action.payload;
      state.getProgressionSessions.subQuestId = subQuestId;
      state.getProgressionSessions.progressionId = progressionId;
    },
    // Reducer to set values for deleting a progression
    setDeleteProgressionValue(state, action) {
      const { subQuestId, progressionId } = action.payload;
      state.deleteProgression.subQuestId = subQuestId;
      state.deleteProgression.progressionId = progressionId;
    },
    // Reducer to set values for deleting a progression session
    setDeleteProgressionSessionValue(state, action) {
      const { subQuestId, progressionId, sessionId } = action.payload;
      state.deleteProgressionSession.subQuestId = subQuestId;
      state.deleteProgressionSession.progressionId = progressionId;
      state.deleteProgressionSession.sessionId = sessionId;
    },
    // Reducer to reset the delete progression state
    resetDeleteProgressionStatus(state) {
      state.deleteProgression = {
        modal: false,
        subQuestId: null,
        progressionId: null,
        status: ReducerStatus.IDLE,
        error: null,
      };
    },
    // Reducer to set values for editing a progression session
    setUpdateQuizSession(state, action) {
      state.updateQuizSession = {
        ...state.updateQuizSession,
        ...action.payload,
      };
    },
    // Reducer to reset the delete progression session state
    resetDeleteProgressionSessionStatus(state) {
      state.deleteProgressionSession = {
        modal: false,
        subQuestId: null,
        progressionId: null,
        sessionId: null,
        status: ReducerStatus.IDLE,
        error: null,
      };
    },
    setExportSubmissionSessions(state, action) {
      state.exportSubmissionSessions = {
        ...state.exportSubmissionSessions,
        ...action.payload,
      };
    },
    // Reducer to set values for the import submission sessions modal
    setImportSubmissionSessions(state, action) {
      state.importSubmissionSessions = {
        ...state.importSubmissionSessions,
        ...action.payload,
      };
    },
  },
  extraReducers(builder) {
    builder
      // Handle fetching sub-quest by ID
      .addCase(getSubQuestByIdAction.pending, (state) => {
        state.getSubQuest.status = ReducerStatus.LOADING;
      })
      .addCase(getSubQuestByIdAction.fulfilled, (state, action) => {
        state.getSubQuest.status = ReducerStatus.SUCCEEDED;
        state.subQuest = action.payload;
      })
      .addCase(getSubQuestByIdAction.rejected, (state, action) => {
        state.getSubQuest.status = ReducerStatus.FAILED;
        state.getSubQuest.error = action.payload;
      })

      // Handle fetching sub-quest progressions
      .addCase(getSubQuestProgressionsAction.pending, (state) => {
        state.getProgressions.status = ReducerStatus.LOADING;
      })
      .addCase(getSubQuestProgressionsAction.fulfilled, (state, action) => {
        state.getProgressions.status = ReducerStatus.SUCCEEDED;
        const { metaData, data } = action.payload;
        state.progressions = data;
        state.progressionMetaData = metaData;
      })
      .addCase(getSubQuestProgressionsAction.rejected, (state, action) => {
        state.getProgressions.status = ReducerStatus.FAILED;
        state.getProgressions.error = action.payload;
      })

      // Handle fetching sub-quest logs
      .addCase(getSubQuestLogsAction.pending, (state) => {
        state.getLogs.status = ReducerStatus.LOADING;
      })
      .addCase(getSubQuestLogsAction.fulfilled, (state, action) => {
        state.getLogs.status = ReducerStatus.SUCCEEDED;
        const { data } = action.payload;
        state.logs = data;
      })
      .addCase(getSubQuestLogsAction.rejected, (state, action) => {
        state.getLogs.status = ReducerStatus.FAILED;
        state.getLogs.error = action.payload;
      })

      // Handle fetching sub-quest progressions count
      .addCase(getSubQuestProgressionsCountAction.pending, (state) => {
        state.getProgressionsCount.status = ReducerStatus.LOADING;
      })
      .addCase(
        getSubQuestProgressionsCountAction.fulfilled,
        (state, action) => {
          state.getProgressionsCount.status = ReducerStatus.SUCCEEDED;
          state.progressionsCount = action.payload;
        }
      )

      // Handle fetching sub-quest progression sessions
      .addCase(getSubQuestProgressionsCountAction.rejected, (state, action) => {
        state.getProgressionsCount.status = ReducerStatus.FAILED;
        state.getSubQuest.error = action.payload;
      })

      .addCase(getSubQuestProgressionSessionsAction.pending, (state) => {
        state.getProgressionSessions.status = ReducerStatus.LOADING;
      })
      .addCase(
        getSubQuestProgressionSessionsAction.fulfilled,
        (state, action) => {
          state.getProgressionSessions.status = ReducerStatus.SUCCEEDED;
          state.progressionSessions = action.payload;
        }
      )
      .addCase(
        getSubQuestProgressionSessionsAction.rejected,
        (state, action) => {
          state.getProgressionSessions.status = ReducerStatus.FAILED;
          state.getProgressionSessions.error = action.payload;
        }
      )

      // Handle deleting a sub-quest progression session
      .addCase(deleteSubQuestProgressionSessionsAction.pending, (state) => {
        state.deleteProgressionSession.status = ReducerStatus.LOADING;
      })
      .addCase(
        deleteSubQuestProgressionSessionsAction.fulfilled,
        (state, action) => {
          state.deleteProgressionSession.status = ReducerStatus.SUCCEEDED;

          // Remove the session with the matching ID from the progression sessions array
          state.progressionSessions = state.progressionSessions.filter(
            (session) => session.id !== action.payload
          );
        }
      )
      .addCase(
        deleteSubQuestProgressionSessionsAction.rejected,
        (state, action) => {
          state.deleteProgressionSession.status = ReducerStatus.FAILED;
          state.deleteProgressionSession.error = action.payload;
        }
      )

      // Handle deleting a sub-quest progression
      .addCase(deleteSubQuestProgressionAction.pending, (state) => {
        state.deleteProgression.status = ReducerStatus.LOADING;
      })
      .addCase(deleteSubQuestProgressionAction.fulfilled, (state, action) => {
        state.deleteProgression.status = ReducerStatus.SUCCEEDED;
        state.deleteProgression.subQuestId = null;
        // Remove the progression with the matching ID from the progressions array
        state.progressions = state.progressions.filter(
          (progression) => progression.id !== action.payload
        );
      })
      .addCase(deleteSubQuestProgressionAction.rejected, (state, action) => {
        state.deleteProgression.status = ReducerStatus.FAILED;
        state.deleteProgression.error = action.payload;
      })

      // Handle fetching a single sub-quest progression session
      .addCase(getSubQuestProgressionSessionAction.pending, (state) => {
        state.getProgressionSession.status = ReducerStatus.LOADING;
        state.getProgressionSession.error = null;
        state.progressionSession = null;
      })
      .addCase(
        getSubQuestProgressionSessionAction.fulfilled,
        (state, action) => {
          state.getProgressionSession.status = ReducerStatus.SUCCEEDED;
          state.getProgressionSession.error = null;
          state.progressionSession = action.payload;
        }
      )
      .addCase(
        getSubQuestProgressionSessionAction.rejected,
        (state, action) => {
          state.getProgressionSession.status = ReducerStatus.FAILED;
          state.getProgressionSession.error = action.payload;
          state.progressionSession = null;
        }
      )

      // Handle updating a quiz session
      .addCase(updateQuizSessionAction.pending, (state) => {
        state.updateQuizSession.status = ReducerStatus.LOADING;
        state.updateQuizSession.error = null;
      })
      .addCase(updateQuizSessionAction.fulfilled, (state, action) => {
        state.updateQuizSession.status = ReducerStatus.SUCCEEDED;
        state.updateQuizSession.error = null;

        // Update the session in progressionSessions array if it exists
        const sessionIndex = state.progressionSessions.findIndex(
          (session) => session.id === action.payload.id
        );
        if (sessionIndex !== -1) {
          state.progressionSessions[sessionIndex] = action.payload;
        }

        // Update the single progressionSession if it matches
        if (
          state.progressionSession &&
          state.progressionSession.id === action.payload.id
        ) {
          state.progressionSession = action.payload;
        }
      })
      .addCase(updateQuizSessionAction.rejected, (state, action) => {
        state.updateQuizSession.status = ReducerStatus.FAILED;
        state.updateQuizSession.error = action.payload;
      })

      // Handle fetching bulk file submissions
      .addCase(getBulkFileSubmissionsAction.pending, (state) => {
        state.exportSubmissionSessions.status = ReducerStatus.LOADING;
        state.exportSubmissionSessions.error = null;
        state.exportSubmissionSessions.data = [];
      })
      .addCase(getBulkFileSubmissionsAction.fulfilled, (state, action) => {
        state.exportSubmissionSessions.status = ReducerStatus.SUCCEEDED;
        state.exportSubmissionSessions.error = null;
        state.exportSubmissionSessions.data = action.payload;
      })
      .addCase(getBulkFileSubmissionsAction.rejected, (state, action) => {
        state.exportSubmissionSessions.status = ReducerStatus.FAILED;
        state.exportSubmissionSessions.error = action.payload;
        state.exportSubmissionSessions.data = [];
      })

      // Handle fetching progression user IDs
      .addCase(getProgressionUserIdsAction.pending, (state) => {
        state.getProgressionUserIds.status = ReducerStatus.LOADING;
        state.getProgressionUserIds.error = null;
        state.getProgressionUserIds.data = [];
      })
      .addCase(getProgressionUserIdsAction.fulfilled, (state, action) => {
        state.getProgressionUserIds.status = ReducerStatus.SUCCEEDED;
        state.getProgressionUserIds.error = null;
        state.getProgressionUserIds.data = action.payload;
      })
      .addCase(getProgressionUserIdsAction.rejected, (state, action) => {
        state.getProgressionUserIds.status = ReducerStatus.FAILED;
        state.getProgressionUserIds.error = action.payload;
        state.getProgressionUserIds.data = [];
      })

      // Handle importing bulk file submissions
      .addCase(importBulkFileSubmissionsAction.pending, (state) => {
        state.importSubmissionSessions.status = ReducerStatus.LOADING;
        state.importSubmissionSessions.error = null;
        state.importSubmissionSessions.result = null;
      })
      .addCase(importBulkFileSubmissionsAction.fulfilled, (state, action) => {
        state.importSubmissionSessions.status = ReducerStatus.SUCCEEDED;
        state.importSubmissionSessions.result = action.payload;
      })
      .addCase(importBulkFileSubmissionsAction.rejected, (state, action) => {
        state.importSubmissionSessions.status = ReducerStatus.FAILED;
        state.importSubmissionSessions.error = action.payload;
      });
  },
});

// Export actions for use in components
export const subQuestReportActions = subQuestReportSlice.actions;

// Export the slice reducer
export default subQuestReportSlice;
